// app-pages/EmailingPage.tsx
"use client";
import {
  Box,
  Text,
  Input,
  Button,
  Flex,
  VStack,
  HStack,
  Image,
  Icon,
  useToast,
  Show,
} from "@chakra-ui/react";
import DeepMode from "@public/DeepMode.svg";
import React from "react";
import { CheckCircleIcon } from "@chakra-ui/icons"; // Assuming you're using Chakra's icon library
import axios from "axios";
import Link from "next/link";

interface PricePlan {
  title: string;
  price: string;
  features: string[];
  bg: string;
}

interface PricePlanCardProps {
  title: string;
  price: string;
  features: string[];
  bg: string;
}

export function EmailingPage() {
  const [email, setEmail] = React.useState("");
  const toast = useToast();

  // This function will be called when the "Apply" button is clicked
  const handleApplyClick = async () => {
    try {
      const checkResponse = await axios.get(
        `https://api.airtable.com/v0/appa57PPbGpEoMIv6/Table%201?filterByFormula={Email}='${email}'`,
        {
          headers: {
            Authorization:
              "Bearer patlNaVmrI8P6FIAk.82883ef9ae6a0cb1da4af8004fc6bcf1f237b2d2d06d30ee98a35b30e4ffee72",
          },
        }
      );

      // If email exists, notify user and return
      if (checkResponse.data.records.length > 0) {
        toast({
          title: "Email already registered",
          description: "This email is already on our waiting list.",
          status: "info",
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      const response = await axios.post(
        "https://api.airtable.com/v0/appa57PPbGpEoMIv6/Table%201", // Ensure that "Table 1" is URL-encoded to "Table%201"
        {
          records: [
            {
              fields: {
                Email: email, // Make sure the field name "Email" matches the field name in your Airtable base
              },
            },
          ],
        },
        {
          headers: {
            Authorization:
              "Bearer patlNaVmrI8P6FIAk.82883ef9ae6a0cb1da4af8004fc6bcf1f237b2d2d06d30ee98a35b30e4ffee72",
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        toast({
          title: "Success",
          description: "Email added successfully! Thank you for joining us.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error(error);
      alert("Error adding email. Please try again.");
    }
  };

  const pricePlans: PricePlan[] = [
    {
      title: "STARTER",
      price: "FREE",
      features: ["5 Free Trial Tokens", "Generate 20 photos for free"],
      bg: "rgba(113, 46, 255, 0.25)",
    },
    {
      title: "Pro",
      price: "$29 /month",
      features: ["250 Token Renew Monthly", "Generate 1,000 photos each month."],
      bg: "rgba(113, 46, 255, 0.65)",
    },
    {
      title: "Premium",
      price: "$49 /month",
      features: ["500 Tokens Renew Monthly", "Generate 2,000 photos each month."],
      bg: "rgba(113, 46, 255, 1)",
    },
  ];

  const PricePlanCard: React.FC<PricePlanCardProps> = ({ title, price, features, bg }) => {
    return (
      <Box backgroundColor={bg} p="6" width={{ base: "310px", xl: "330px" }}>
        <Text textAlign="start" fontSize="14px" fontWeight={700} color="white" mb="4">
          {title}
        </Text>
        <VStack align="start" spacing={3}>
          <Box display="flex" alignItems="baseline">
            <Text color="white" fontSize="3xl" fontWeight="bold" mr="2">
              {price.includes("/") ? price.split("/")[0] : price}
            </Text>
            {price.includes("/") && (
              <Text color="white" fontSize="xl" fontWeight="normal">
                /{price.split("/")[1]}
              </Text>
            )}
          </Box>
          {features.map((feature, index) => (
            <Flex align="center" key={index}>
              {feature.startsWith("Generate") && <Icon as={CheckCircleIcon} color="white" mr="2" />}
              <Text
                fontWeight={feature.startsWith("Generate") ? 300 : 600}
                fontSize="12px"
                color="white"
              >
                {feature}
              </Text>
            </Flex>
          ))}
        </VStack>
      </Box>
    );
  };

  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      height="100%"
      backgroundColor="#1E1015"
      minH="100vh"
      justify="center"
    >
      <Image mt="80px" w="171px" h="38px" src={DeepMode.src} alt="DeepMode Logo" />
      <Link target="_blank" href="https://tally.so/r/n9Q2Wp">
        <Text
          mt="60px"
          bgGradient="linear(to-r, #FFE1FF, #A4DBEE)"
          bgClip="text"
          fontWeight={700}
          fontSize={{ base: "32px", lg: "68px" }}
        >
          Join the waiting list
        </Text>
      </Link>
      <VStack mt="6">
        <Input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your email"
          size="lg"
          width={{ base: "250px", xl: "400px" }}
          backgroundColor="transparent"
          border="1px solid #B1DCF1"
          borderRadius="8px"
          _placeholder={{
            color: "#B1DCF1",
          }}
          color="white"
        />
        <Button
          onClick={handleApplyClick}
          bg="#712EFF"
          h="50px"
          color="white"
          mt="10px"
          borderRadius="10px"
          width="225px"
        >
          Apply
        </Button>
      </VStack>
      <Box mt="10" textAlign="center">
        <Text
          mt="25px"
          bgGradient="linear(to-r, #FFE1FF, #A4DBEE)"
          bgClip="text"
          fontWeight={700}
          fontSize={{ base: "32px", xl: "68px" }}
        >
          Pricing Plan
        </Text>
        {/* Mobile, < 768px */}
        <Show below="md-1">
          <VStack mt="20px" spacing="0">
            {pricePlans.map((plan, index) => (
              <PricePlanCard
                key={index}
                title={plan.title}
                price={plan.price}
                features={plan.features}
                bg={plan.bg}
              />
            ))}
          </VStack>
        </Show>
        {/* Desktop, >= 768px */}
        <Show above="md">
          <HStack mt="20px" spacing="0">
            {pricePlans.map((plan, index) => (
              <PricePlanCard
                key={index}
                title={plan.title}
                price={plan.price}
                features={plan.features}
                bg={plan.bg}
              />
            ))}
          </HStack>
        </Show>
        <Box h="200px"></Box>
      </Box>
    </Flex>
  );
}

export default EmailingPage;
